/**
 * The wallet stack, mounted on demand, and the only place actions live.
 *
 * `app/providers.tsx` deliberately has no `WagmiProvider` — mounting one there
 * would pull the wallet chunk into the critical path for every anonymous
 * visitor, which is the opposite of what §5.1 asks for and what Phase 4's
 * first-paint criterion was measured against.
 *
 * ## The split that makes lazy loading invisible to components
 *
 * ```
 *   Runtime.identityStore   eager, viem-free   ← components READ this
 *   Runtime.ensureIdentity() dynamic import()  ← components ACT through this
 * ```
 *
 * `<ConnectButton>` renders from the store at cold load, before the wallet chunk
 * exists, and shows `Connect` — which is the correct anonymous state and not a
 * placeholder. Every action routes through this provider, which awaits
 * `ensureIdentity()` first. So no component branches on "has the chunk loaded",
 * and there is no second render path for the moment before it has.
 */

import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Runtime } from '@/app/runtime'
import { useStore } from '@/app/use-store'
import type { IdentityState } from '@/wallet/identity'
import { fundingFrom, targetsFrom, type ChainTargets } from '@/wallet/chain-targets'
import { canSwitchTo, chainName, pinOf, verifyChainTargets, type ChainTrustVerdict } from '@/domain/chain-trust'
import { forgetPin, rememberPin, seenPin } from '@/wallet/chain-trust-store'
import { toEip712Domain } from '@/wallet/eip712'
import { CHAIN_PINS } from '@/lib/env'
import { formatFixed, USDC_DP } from '@/lib/decimal'
import { HttpError } from '@/api/http/problem'
// **A type-only namespace import, and the `type` keyword is load-bearing.** It
// is erased before the bundler sees it, so this file's only runtime edge to the
// wrapper's module (B8) is the `import()` inside `sendWrapper` below. A value
// import here would put viem on every anonymous visitor's cold load
// (invariant 13, §F21k). `@/wallet/funding` is reached the same way.
import type * as UsduModule from '@/wallet/usdu'
import { UNREAD_WRAPPER, type WrapperState } from '@/wallet/usdu'

import { loadWalletConfig } from '@/wallet/lazy'
import { untilMined } from '@/wallet/receipt'
import { RotationScheduler } from '@/wallet/rotation-scheduler'
import type { WalletConfig } from '@/wallet/connectors'
import type { ConnectorOption } from './ConnectModal'
import { ConnectModal, SignInModal } from './ConnectModal'
import { TradingSetupModal } from './TradingSetupModal'

/**
 * **Loaded on intent, never on cold load** — `routes/trade/panels.tsx`'s rule
 * for the pro ladder, and the cold-load budget's (invariant 19). Choosing a
 * username happens once per account, so its window is not something every
 * visitor should download.
 */
const ChooseUsernameModal = lazy(() => import('./ChooseUsernameModal'))
import { missingSetup, type SetupStep } from '@/domain/trading-setup'
import { withdrawalReceiptOf, type WithdrawalReceipt } from '@/domain/unwrap'

/** Which `/money` panel a funding failure belongs to. */
export type FundingAction = 'ADD' | 'WITHDRAW'

/**
 * Why the last add or withdrawal failed, for the panel the user clicked.
 *
 * `error` is read only by the connect, sign-in and setup modals, so a failure
 * on `/money` was set and never shown — a rate-limited "Add funds" looked like
 * a button that did nothing (2026-10-20). `id` only ever grows: a panel shows a
 * failure newer than the one it found when it mounted, so an old one never
 * reappears on a later visit.
 */
export interface FundingFailure {
  id: number
  action: FundingAction
  message: string
}

export interface WalletContextValue {
  connect: () => void
  signIn: () => void
  /**
   * Opens the trading-setup modal at the first step still missing — the session
   * key, then wrap and approve (`domain/trading-setup.ts`). Called
   * from the ticket's first focus and its `ENABLE TRADING` button, which passes
   * what the order it is composing needs so the wrap step is judged against it.
   * Anything that is not a `bigint` is ignored, so it can be an `onClick`.
   */
  promptEnableTrading: (neededMicroUsdc?: unknown) => void
  /**
   * The same modal, for a wallet on the wrong network: it opens, switches the
   * network, and asks for the session-key signature once the switch lands. One
   * click, from the ticket's `SWITCH NETWORK`.
   */
  switchAndEnableTrading: (neededMicroUsdc?: unknown) => void
  /**
   * Ask the wallet to switch to the venue's chain, and add it first if the
   * wallet does not have it. **Only from a button that names the network**:
   * nothing switches the user's chain without a click. Resolves `null` once the
   * wallet is on the venue's chain, or the sentence to show if it is not.
   */
  switchToVenueChain: () => Promise<string | null>
  /**
   * Opens the choose-a-username window (`docs/06` §F136) — from the account
   * menu and from Settings. No signature: a claim is one request on the
   * session the user already has.
   */
  chooseUsername: () => void
  logout: () => void
  revoke: (keyAddress: string, opts: { cancelOrders: boolean }) => Promise<void>
  busy: boolean

  // --- funding (`backend/GASLESS-PLAN.md`) --------------------------------
  //
  // **Adding funds is the whole of it.** A deposit lets the settlement
  // contract collect the user's losses from the trading balance, so there is
  // no approval to grant, read or top up — that state lived here until gasless
  // funding. These live here for every other action's reason: the wallet chunk
  // is behind `loadWalletConfig()` and no component may branch on whether it
  // has arrived.

  /**
   * Re-read the wallet's balances for the ticket's setup gate. A no-op until
   * the wallet stack has loaded unless `load` is set — the ticket reads on
   * mount only when a wallet is already in hand, and loads one on focus, which
   * is intent (invariant 13).
   */
  refreshChainState: (load?: boolean) => Promise<void>
  /**
   * Whether this venue sends funding transactions for the user
   * (`config.chain.relay.enabled`). With it, "Add funds" and "Withdraw" are one
   * signature each and no gas; without it, the direct path is the only one.
   */
  relayEnabled: boolean
  /**
   * "Add funds": one signature, no gas, when the venue relays — otherwise the
   * direct path (`approveBacking` then `deposit`). Resolves `true` once the
   * money is in the trading balance.
   */
  addFunds: (amountMicroUsdc: bigint) => Promise<boolean>
  /**
   * "Withdraw": one signature, no gas — otherwise the direct `requestUnwrap`.
   * Through the venue, the part in no market is paid in the same transaction
   * and the rest is set aside until its markets settle, when the venue
   * finishes it (§F126). Resolves `false` if nothing moved; once it has
   * mined, the venue's receipt — what reached the wallet at once — or `true`
   * on the direct path, or for a receipt that did not parse.
   */
  withdraw: (amountMicroUsdc: bigint) => Promise<WithdrawalReceipt | boolean>
  /**
   * The chain the venue's contracts are on, when it differs from the wallet's.
   * Kept current by the wallet's own `chainChanged`, so a switch made in the
   * wallet's UI updates every panel without another read.
   */
  chainMismatch: { wallet: number; venue: number } | null
  /**
   * Whether the contracts `GET /config` names may be acted on at all (§F48d).
   *
   * Rendered by the screens that move money and **enforced by the send paths
   * below**, which is the half that matters: a panel that only displayed this
   * would be a warning beside a working button.
   */
  chainTrust: ChainTrustVerdict
  /**
   * Accept a changed address pair and remember it (`ADDRESSES_CHANGED` only).
   *
   * Deliberately a separate action rather than a flag on a send: agreeing that
   * the venue's exchange has moved is a decision, and a decision made by
   * clicking Deposit is not one.
   */
  trustCurrentAddresses: () => void
  /** The last add or withdrawal that failed, either path; cleared when the next starts. */
  fundingFailure: FundingFailure | null

  // --- the direct path (B8) -------------------------------------------------
  //
  // The same money movements, sent from the user's own wallet at their own
  // gas: "Send from my wallet instead". `wallet/usdu.ts` is the whole argument.

  /** Both balances, USDC's allowance to USDU, and the account's standing. `null` is *unread*. */
  wrapper: WrapperState
  /**
   * Re-read from the chain. Safe to call when disconnected — it no-ops.
   * `atBlock` pins the read to the block a pushed change landed in, which the
   * wallet answers fresh where its `latest` may be seconds old.
   */
  refreshWrapper: (atBlock?: bigint) => Promise<void>
  /** Direct path: let USDU take `amount` micro-USDC. Bounded; there is no infinite path. */
  approveBacking: (amountMicroUsdc: bigint) => Promise<void>
  /** Direct path: add funds, exactly. Resolves `true` once it has mined. */
  deposit: (amountMicroUsdc: bigint) => Promise<boolean>
  /**
   * Direct path: ask to withdraw (§F76's `requestUnwrap`). The amount leaves
   * the trading balance at once and is paid out by `finishUnwrap` once its
   * markets have settled (§F126) — the venue's relayer, where there is one,
   * pays the part in no market within seconds and finishes the rest. Refused
   * on chain unless `NORMAL`. Resolves `true` once it has mined, so the panel
   * can clear its field.
   */
  requestUnwrap: (amountMicroUsdc: bigint) => Promise<boolean>
  /** Pay out the pending withdrawal as USDC, once its markets have settled (or the backstop has passed). */
  finishUnwrap: () => Promise<boolean>
  /** Put the pending withdrawal back into the trading balance. */
  cancelUnwrap: () => Promise<boolean>
}

const WalletContext = createContext<WalletContextValue | null>(null)

export function useWallet(): WalletContextValue {
  const value = useContext(WalletContext)
  if (!value) throw new Error('useWallet must be used inside <WalletProvider>')
  return value
}

export interface WalletProviderProps {
  runtime: Runtime
  children: ReactNode
}

export function WalletProvider({ runtime, children }: WalletProviderProps) {
  const identity: IdentityState = useStore(runtime.identityStore)
  const queryClient = useQueryClient()
  const [config, setConfig] = useState<WalletConfig | null>(null)
  const [picking, setPicking] = useState(false)
  const [signingIn, setSigningIn] = useState(false)
  const [enabling, setEnabling] = useState(false)
  const [choosingName, setChoosingName] = useState(false)
  const chooseUsername = useCallback(() => setChoosingName(true), [])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fundingFailure, setFundingFailure] = useState<FundingFailure | null>(null)
  const fundingFailures = useRef(0)
  const failFunding = useCallback((action: FundingAction, message: string) => {
    setError(message)
    fundingFailures.current += 1
    setFundingFailure({ id: fundingFailures.current, action, message })
  }, [])
  const [connectors, setConnectors] = useState<readonly ConnectorOption[]>([])

  const connect = useCallback(() => {
    setError(null)
    setPicking(true)
    void loadWalletConfig()
      .then((loaded) => {
        setConfig(loaded)
        setConnectors(loaded.connectors.map((c) => ({ id: c.id, name: c.name, icon: c.icon })))
      })
      .catch((cause: unknown) => {
        setError(messageFor(cause))
      })
  }, [])

  const select = useCallback(
    async (connectorId: string) => {
      if (!config) return
      setBusy(true)
      setError(null)
      try {
        const [{ connect: wagmiConnect, getAccount }, controller] = await Promise.all([
          import('wagmi/actions'),
          runtime.ensureIdentity(),
        ])
        const connector = config.connectors.find((c) => c.id === connectorId)
        if (!connector) throw new Error('That wallet is no longer available.')

        // `loadWalletConfig()` reconnects a previously-authorised wallet, so by
        // the time the picker is on screen we may already hold an account.
        // Calling `connect()` again throws `Connector already connected` and
        // strands the user in the picker looking at an SDK error — for a wallet
        // that is, in fact, connected. Reuse it instead.
        const existing = getAccount(config)
        const result =
          existing.address && existing.connector?.id === connectorId ?
            { accounts: [existing.address] as const, chainId: existing.chainId ?? 0 }
          : await wagmiConnect(config, { connector })

        const address = result.accounts[0]
        if (!address) throw new Error('The wallet returned no account.')
        controller.store.patch({
          phase: 'CONNECTED',
          address,
          // Written from the wallet, which is the only source allowed to write
          // it: `accountMismatch` is meaningless if a server response can set
          // both sides of the comparison (`docs/17`).
          walletAddress: address,
          chainId: result.chainId,
          error: null,
        })
        setPicking(false)
        // Straight into SIWE: two consecutive modals (docs/04 §3.1), because the
        // user's intent was "sign in" and making them hunt for a second button
        // is friction with no security value.
        setSigningIn(true)
      } catch (cause: unknown) {
        setError(messageFor(cause))
      } finally {
        setBusy(false)
      }
    },
    [config, runtime],
  )

  const signIn = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      const loaded = config ?? (await loadWalletConfig())
      setConfig(loaded)
      const [{ getAccount, signMessage, signTypedData }, controller] = await Promise.all([
        import('wagmi/actions'),
        runtime.ensureIdentity(),
      ])
      const account = getAccount(loaded)
      if (!account.address) throw new Error('Connect a wallet first.')
      await controller.signIn({
        address: account.address,
        chainId: account.chainId ?? 0,
        signMessage: ({ message }) => signMessage(loaded, { message }),
        signTypedData: (args) =>
          signTypedData(loaded, args as unknown as Parameters<typeof signTypedData>[1]),
      })
      setSigningIn(false)
    } catch (cause: unknown) {
      setError(messageFor(cause))
    } finally {
      setBusy(false)
    }
  }, [config, runtime])

  // The scheduler is created once per runtime and must not be torn down and
  // rebuilt whenever `arm` or `busy` changes identity — a scheduler that
  // restarts loses the window it was sleeping towards. Refs carry the current
  // values in without making them dependencies. Written in effects, never during
  // render (`react-hooks` forbids the latter, and it tears under concurrent
  // rendering).
  const armRef = useRef<() => Promise<void>>(async () => {})
  const busyRef = useRef(false)
  // For the account watcher, which subscribes once per config. Both are
  // declared further down.
  const noteChainRef = useRef<(walletChainId: number | null) => void>(() => {})
  const refreshWrapperRef = useRef<(atBlock?: bigint) => Promise<void>>(async () => {})

  const arm = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      const loaded = config ?? (await loadWalletConfig())
      setConfig(loaded)
      const [{ getAccount, signTypedData }, controller] = await Promise.all([
        import('wagmi/actions'),
        runtime.ensureIdentity(),
      ])
      const account = getAccount(loaded)
      if (!account.address) throw new Error('Connect a wallet first.')
      await controller.arm({
        address: account.address,
        chainId: account.chainId ?? 0,
        signTypedData: (args) =>
          signTypedData(loaded, args as unknown as Parameters<typeof signTypedData>[1]),
      })
      // The Sessions screen is reading `GET /auth/session-keys` through
      // react-query, and the key it should now be showing did not exist when
      // that query last ran. Without this the user signs, the modal closes, and
      // the list they are looking at is unchanged — which reads as a failure.
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session-keys'] })
      // Not closed here: the setup modal moves on to whatever funding step is
      // still missing, and closes itself when nothing is.
    } catch (cause: unknown) {
      setError(messageFor(cause))
    } finally {
      setBusy(false)
    }
  }, [config, runtime, queryClient])

  /**
   * Follow the wallet's own account, which nothing in this tree did before.
   *
   * The two signing paths record `walletAddress` as they go, but a user
   * switching account in MetaMask raises no popup and touches nothing here — so
   * without this the tab keeps trading the account it signed in as, indefinitely
   * and invisibly, because every panel renders the session's address and so does
   * the order's `maker` (`docs/17`).
   *
   * Gated on `config`, which is invariant 13 rather than an optimisation: the
   * wagmi stack loads on intent, so there is nothing to watch on an anonymous
   * cold load and importing one to find that out would put the signing stack in
   * it. `watchAccount` fires on connect, disconnect and account change; a
   * disconnect writes `null`, which `accountMismatch` reads as "nothing to
   * compare" rather than as a mismatch.
   */
  useEffect(() => {
    if (!config) return
    let stop: (() => void) | null = null
    let cancelled = false
    void (async () => {
      const [{ getAccount, watchAccount }, controller] = await Promise.all([
        import('wagmi/actions'),
        runtime.ensureIdentity(),
      ])
      if (cancelled) return
      // The chain a reconnected wallet is already on: there is no change event
      // for that, so without this the header and the ticket would not know
      // until the first balance read.
      const initial = getAccount(config)
      if (initial.address) noteChainRef.current(initial.chainId ?? null)
      stop = watchAccount(config, {
        onChange: (account, previous) => {
          controller.store.applyWallet({
            address: account.address ?? null,
            chainId: account.chainId ?? null,
          })
          // A network switch, ours or one made in the wallet's own UI: say so at
          // once, and re-read the balances, which were unreadable on the old chain.
          if (account.address && account.chainId !== previous.chainId) {
            noteChainRef.current(account.chainId ?? null)
            void refreshWrapperRef.current()
          }
        },
      })
    })()
    return () => {
      cancelled = true
      stop?.()
    }
  }, [config, runtime])

  useEffect(() => {
    armRef.current = arm
  }, [arm])

  useEffect(() => {
    busyRef.current = busy || enabling
  }, [busy, enabling])

  const logout = useCallback(() => {
    void runtime
      .ensureIdentity()
      .then((controller) => controller.logout())
      .then(() => queryClient.removeQueries({ queryKey: ['auth'] }))
  }, [runtime, queryClient])

  const revoke = useCallback(
    async (keyAddress: string, opts: { cancelOrders: boolean }) => {
      const controller = await runtime.ensureIdentity()
      await controller.revoke(keyAddress, opts)
    },
    [runtime],
  )

  /**
   * Automatic rotation — plan §Phase 5: "rotation at `T+20` when expiry is
   * inside 120s. **Never at a roll boundary**".
   *
   * `rotation.ts` decides *whether*; `RotationScheduler` decides *when to ask*;
   * this supplies the two things neither can know — the market the page is
   * pointed at, and how to raise a wallet popup. `arm()` is reused verbatim, so
   * a rotation is a registration and there is no second signing path to keep in
   * step.
   *
   * `isBusy` is the guard that keeps the popup budget honest: a rotation must
   * never queue behind a sign-in the user is part-way through, and `enabling`
   * counts because the modal is already asking for the same signature.
   */
  useEffect(() => {
    const scheduler = new RotationScheduler({
      getTiming: () => {
        const market = runtime.state.state
        const identityState = runtime.identityStore.state
        return {
          keyExpiresAtMs: identityState.keyExpiresAtMs,
          nowMs: runtime.clock.now(),
          anchorS: market.anchorS,
          periodS: market.periodS,
        }
      },
      onRotate: () => armRef.current(),
      isBusy: () => busyRef.current || runtime.identityStore.state.walletBusy,
    })
    scheduler.start()

    // The schedule is derived from key state the scheduler does not own, and
    // when nothing is due it sleeps for ten minutes. Without this, a key that
    // becomes due inside that sleep waits it out — eventually correct, because
    // the recheck is shorter than the lead time, but it leaves a user who just
    // armed on a ten-minute-stale timer.
    const unsubscribe = runtime.identityStore.subscribe(() => scheduler.reschedule())

    return () => {
      unsubscribe()
      scheduler.stop()
    }
  }, [runtime])

  /**
   * `docs/07` §2's bracketed remedies, wired to the surfaces that own them.
   *
   * A rejection toast that says "enable a new one" needs a button that does it,
   * and the only place that can raise a wallet popup is here. `OrderController`
   * therefore names an *action* rather than reaching for a modal — it holds no
   * React and must not learn to.
   */
  useEffect(() => {
    runtime.setErrorActionHandler((action) => {
      switch (action) {
        case 'SIGN_IN':
          setSigningIn(true)
          return
        case 'ENABLE_TRADING':
          setEnabling(true)
          return
        case 'CANCEL_ALL':
          void runtime.trading.cancelAll()
          return
        case 'SUPPORT':
          // A mailto rather than an in-app surface: there is no support console
          // in v1, and a button that opens nothing is worse than no button.
          globalThis.location.assign('mailto:support@updown.xyz')
      }
    })
  }, [runtime])

  // Keep the key band current even when nothing is due — it is what moves the
  // header from "trading enabled" to the renewal prompt.
  useEffect(() => {
    const id = globalThis.setInterval(() => runtime.identity?.refreshKeyState(), 30_000)
    return () => globalThis.clearInterval(id)
  }, [runtime])

  // --- chain state -----------------------------------------------------------

  const [wrapper, setWrapper] = useState<WrapperState>(UNREAD_WRAPPER)
  // The account it was last read for. A read for the same account keeps what
  // an earlier read knew wherever this one came back empty — see `keepKnown`.
  const wrapperFor = useRef<string | null>(null)
  const [chainMismatch, setChainMismatch] = useState<{ wallet: number; venue: number } | null>(null)
  const [chainTrust, setChainTrust] = useState<ChainTrustVerdict>(() =>
    verifyChainTargets({ targets: null }),
  )

  /**
   * Is this venue's set of contracts one this browser will act on? (§F48d)
   *
   * Computed here rather than held, and re-computed on **every** send, because
   * two of its inputs move underneath it: `/config` is re-fetched when the
   * heartbeat's `config_revision` changes, and the wallet's chain changes when
   * the user switches network in their wallet with the panel already open. A
   * verdict read from state at click time is a verdict about a payload that may
   * not be the one this transaction will name.
   *
   * The EIP-712 domain goes in beside the addresses on purpose: the exchange is
   * published twice, once as `chain.exchange_address` and once inside every
   * order digest, and comparing the two is the cheapest disagreement to detect.
   * `toEip712Domain` is the rename — the wire spells it `verifying_contract`
   * and the hash spells it `verifyingContract` (§F21l).
   */
  const verifyChain = useCallback(
    (walletChainId: number | null): { verdict: ChainTrustVerdict; targets: ChainTargets | null } => {
      const config = runtime.config as { eip712?: { domain?: unknown } } | null
      const targets = targetsFrom(runtime.config)
      const domain = toEip712Domain(config?.eip712?.domain) as {
        chainId?: number
        verifyingContract?: string
      }
      const verdict = verifyChainTargets({
        targets,
        domain,
        walletChainId,
        pins: CHAIN_PINS,
        seen: targets ? seenPin(targets.chainId) : null,
      })
      // Remembered only once it has been trusted, and never before — a store
      // written on the way in would agree with whatever it had just been shown.
      if (verdict.trusted && targets) rememberPin(targets.chainId, pinOf(targets))
      setChainTrust(verdict)
      return { verdict, targets }
    },
    [runtime],
  )

  /**
   * The gate every money-moving path goes through.
   *
   * One place, at the `send*` helpers rather than at each of their callers:
   * every funding signature and every direct transaction hands a contract the
   * venue named either the user's money or authority over it, and a gate that
   * has to be remembered per caller is one the next caller will be written
   * without.
   *
   * It reads the wallet's current chain first, because
   * `WALLET_ON_WRONG_CHAIN` is the most common real failure here and a
   * signature or a transaction made on the wrong chain looks like a success and
   * moves nothing.
   */
  const guardChain = useCallback(
    async (loaded: WalletConfig): Promise<ChainTargets> => {
      const { getAccount } = await import('wagmi/actions')
      const walletChainId = getAccount(loaded).chainId ?? null
      const { verdict, targets } = verifyChain(walletChainId)
      // So the refusal below arrives with its way out: `<WrongChainNotice>`.
      setChainMismatch(mismatchOf(targets, walletChainId))
      if (!verdict.trusted || !targets) {
        throw new Error(
          verdict.trusted ? 'This venue has not published its contract addresses.' : verdict.detail,
        )
      }
      return targets
    },
    [verifyChain],
  )

  /**
   * What the screen says about the wallet's chain. Called on every read and on
   * every `chainChanged`. The send paths re-verify through `guardChain` anyway.
   */
  const noteChain = useCallback(
    (walletChainId: number | null) => {
      setChainMismatch(mismatchOf(targetsFrom(runtime.config), walletChainId))
      // The verdict is computed here as well as on every send, so the screen
      // can disable its controls and say why **before** a click.
      verifyChain(walletChainId)
    },
    [runtime, verifyChain],
  )

  useEffect(() => {
    noteChainRef.current = noteChain
  }, [noteChain])

  /**
   * Put the wallet on the venue's chain, or throw the sentence that says why
   * it is not. wagmi's `switchChain` sends `wallet_switchEthereumChain`, and if
   * the wallet does not know the chain (4902) it sends `wallet_addEthereumChain`
   * with viem's definition, then switches.
   *
   * **This runs only from a click on a button that names the network.** A
   * chain is never switched under the user: a switch they did not ask for would
   * change what every other dapp in their wallet is doing. Nothing that runs
   * without a click calls this, including rotation and the first-focus prompt.
   */
  const ensureVenueChain = useCallback(
    async (loaded: WalletConfig): Promise<void> => {
      const targets = targetsFrom(runtime.config)
      if (!targets) return
      const { getAccount, switchChain } = await import('wagmi/actions')
      if (getAccount(loaded).chainId === targets.chainId) return
      const venue = chainName(targets.chainId)
      const manual = `Your wallet could not switch networks from here. Switch to ${venue} in your wallet, then try again.`
      if (!canSwitchTo(targets.chainId)) throw new ChainSwitchError(manual)
      try {
        await switchChain(loaded, { chainId: targets.chainId as WalletConfig['chains'][number]['id'] })
      } catch (cause: unknown) {
        if (isRejection(cause)) throw new ChainSwitchError(`Network not switched. UpTik needs your wallet on ${venue}.`)
        throw new ChainSwitchError(manual)
      }
      // Re-read rather than trust the resolved promise: some wallets resolve
      // the request and then stay where they were.
      const now = getAccount(loaded).chainId ?? null
      noteChain(now)
      if (now !== targets.chainId) throw new ChainSwitchError(manual)
    },
    [runtime, noteChain],
  )

  /**
   * Accept a changed pair. `ADDRESSES_CHANGED` only, and only from a control
   * that showed the user both addresses.
   */
  const trustCurrentAddresses = useCallback(() => {
    const targets = targetsFrom(runtime.config)
    if (!targets) return
    if (chainTrust.trusted || chainTrust.code !== 'ADDRESSES_CHANGED') return
    forgetPin(targets.chainId)
    rememberPin(targets.chainId, pinOf(targets))
    // The refusal a send raised before this decision is no longer true.
    setError(null)
    verifyChain(null)
  }, [chainTrust, runtime, verifyChain])

  // --- the wrapper (B8) ----------------------------------------------------

  /**
   * Read both balances, the wrapper's allowance and the account's standing.
   *
   * A no-op with no wallet or no published collateral address, quietly — a venue
   * with no chain configured is the ordinary development case. What it must
   * never do is leave `null` looking like zero: `WrapperState`'s nullable fields
   * are exactly that distinction, and `<AddFundsPanel>` renders "not read"
   * rather than inviting a deposit of money the user may already have wrapped.
   */
  const refreshWrapper = useCallback(async (atBlock?: bigint) => {
    const targets = targetsFrom(runtime.config)
    if (!targets) return
    try {
      const loaded = config ?? (await loadWalletConfig())
      setConfig(loaded)
      const [{ getAccount }, { readWrapper }] = await Promise.all([
        import('wagmi/actions'),
        import('@/wallet/usdu'),
      ])
      const account = getAccount(loaded)
      if (!account.address) return
      // A wallet on the wrong chain is said here and corrected only by the
      // user's click (`switchToVenueChain`): a signature or transaction made
      // there looks like a success and moves nothing.
      noteChain(account.chainId ?? null)
      const read = await readWrapper(loaded, account.address, targets.collateral, atBlock)
      const same = wrapperFor.current === account.address
      wrapperFor.current = account.address
      // `readAtBlock` is this read's own, never carried from the last one: a
      // read at `latest` has not said which block it saw.
      setWrapper((prev) => ({ ...keepKnown(same ? prev : UNREAD_WRAPPER, read), readAtBlock: read.readAtBlock }))
    } catch (cause: unknown) {
      // A failed read leaves the previous state standing. It has not told us
      // the user holds nothing.
      setError(messageFor(cause))
    }
  }, [config, runtime, noteChain])

  useEffect(() => {
    refreshWrapperRef.current = refreshWrapper
  }, [refreshWrapper])

  /**
   * The header pill's and `/money`'s "Switch to …". On success the watcher
   * re-reads the balances, so the panel continues from where it left off.
   */
  const switchToVenueChain = useCallback(async (): Promise<string | null> => {
    setBusy(true)
    setError(null)
    try {
      const loaded = config ?? (await loadWalletConfig())
      setConfig(loaded)
      await ensureVenueChain(loaded)
      return null
    } catch (cause: unknown) {
      const message = messageFor(cause)
      setError(message)
      return message
    } finally {
      setBusy(false)
    }
  }, [config, ensureVenueChain])

  /**
   * Send a wrapper transaction, then re-read.
   *
   * Re-read rather than assume: `writeContract` returns on *submission*, and a
   * panel that credited a deposit then would be telling the user something the
   * chain has not agreed to.
   */
  const sendWrapper = useCallback(
    async (
      action: FundingAction,
      send: (mod: typeof UsduModule, loaded: WalletConfig, collateral: `0x${string}`) => Promise<`0x${string}`>,
    ) => {
      setBusy(true)
      setError(null)
      setFundingFailure(null)
      try {
        const loaded = config ?? (await loadWalletConfig())
        setConfig(loaded)
        // The chain-trust gate (§F48d), and it belongs here rather than in
        // `<AddFundsPanel>`: adding funds hands the venue's own token contract
        // real USDC, which cannot be undone by revoking anything. `sendRelay`
        // runs the same gate before anything is signed.
        const targets = await guardChain(loaded)
        const mod = await import('@/wallet/usdu')
        // Re-read at the receipt's block: the wallet answers that fresh, where
        // its `latest` may still be a block it cached before this one.
        await refreshWrapper(await untilMined(loaded, await send(mod, loaded, targets.collateral)))
        return true
      } catch (cause: unknown) {
        failFunding(action, messageFor(cause))
        // A revert, or a receipt that never came, may still have moved
        // something; read rather than leave the panel showing a guess.
        void refreshWrapper()
        return false
      } finally {
        setBusy(false)
      }
    },
    [config, failFunding, guardChain, refreshWrapper],
  )

  const approveBacking = useCallback(
    async (amountMicroUsdc: bigint) => {
      // The backing token's address comes from the wrapper itself and is read
      // by `refreshWrapper` — `/config.chain` publishes the collateral and
      // nothing about what backs it. Without the read there is nothing to
      // approve, and saying so beats sending a transaction to `undefined`.
      const backing = wrapper.backingAddress
      if (!backing) {
        failFunding('ADD', 'The collateral contract has not said what backs it yet. Try again in a moment.')
        return
      }
      await sendWrapper('ADD', (mod, loaded, collateral) =>
        mod.approveBacking(loaded, backing, collateral, amountMicroUsdc),
      )
    },
    [failFunding, sendWrapper, wrapper.backingAddress],
  )

  const deposit = useCallback(
    async (amountMicroUsdc: bigint) => {
      return sendWrapper('ADD', (mod, loaded, collateral) => mod.deposit(loaded, collateral, amountMicroUsdc))
    },
    [sendWrapper],
  )

  const requestUnwrap = useCallback(
    async (amountMicroUsdc: bigint) => {
      return sendWrapper('WITHDRAW', (mod, loaded, collateral) =>
        mod.requestUnwrap(loaded, collateral, amountMicroUsdc),
      )
    },
    [sendWrapper],
  )

  const finishUnwrap = useCallback(async () => {
    return sendWrapper('WITHDRAW', async (mod, loaded, collateral) => {
      const { getAccount } = await import('wagmi/actions')
      const account = getAccount(loaded).address
      if (!account) throw new Error('No wallet is connected.')
      return mod.finishUnwrap(loaded, collateral, account)
    })
  }, [sendWrapper])

  const cancelUnwrap = useCallback(async () => {
    return sendWrapper('WITHDRAW', (mod, loaded, collateral) => mod.cancelUnwrap(loaded, collateral))
  }, [sendWrapper])

  // --- gasless funding (`backend/GASLESS-PLAN.md`) ---------------------------

  // **Signed in, too**: the venue relays only for a session, so a wallet that
  // is connected and not signed in is offered the direct path rather than a
  // signature the venue would refuse.
  const relayEnabled = fundingFrom(runtime.config)?.relay.enabled === true && identity.phase === 'AUTHENTICATED'

  /**
   * Sign, hand the signature to the venue, and wait for the transaction it
   * sends. **One wallet popup, and it is a signature**: the venue pays the gas,
   * and the user needs no ETH.
   *
   * Behind the same chain-trust gate as every direct send — the signature
   * names contracts the venue published — and refused, with nothing signed,
   * when the venue names a USDC its own USDU does not hold.
   *
   * A withdrawal resolves to what the venue's receipt says reached the wallet
   * at once (`paid_now`, §F126) — read only after the transaction has mined,
   * so the screen never says money arrived that a revert kept.
   */
  const sendRelay = useCallback(
    async (kind: 'DEPOSIT' | 'WITHDRAWAL', amount: bigint): Promise<WithdrawalReceipt | boolean> => {
      setBusy(true)
      setError(null)
      setFundingFailure(null)
      try {
        const loaded = config ?? (await loadWalletConfig())
        setConfig(loaded)
        await guardChain(loaded)
        const funding = fundingFrom(runtime.config)
        if (!funding?.relay.enabled) {
          throw new Error('This venue does not send funding transactions for you. Use your own wallet instead.')
        }
        if (wrapper.backingAddress && wrapper.backingAddress.toLowerCase() !== funding.backing.toLowerCase()) {
          throw new Error('The venue names a different USDC from the one your trading balance holds. Nothing was signed.')
        }
        const [{ getAccount }, mod] = await Promise.all([import('wagmi/actions'), import('@/wallet/funding')])
        const account = getAccount(loaded).address
        if (!account) throw new Error('No wallet is connected.')
        const nowMs = runtime.clock.now()
        const receipt =
          kind === 'DEPOSIT' ?
            await mod.signDeposit(loaded, funding, account, amount, nowMs).then((signed) =>
              runtime.api.wallet.relayDeposit({
                amount: formatFixed(amount, USDC_DP),
                valid_after_ms: 0,
                valid_before_ms: Number(signed.validBeforeS) * 1000,
                nonce: signed.nonce,
                signature: signed.signature,
              }),
            )
          : await mod.signWithdraw(loaded, funding, account, amount, nowMs).then((signed) =>
              runtime.api.wallet.relayWithdraw({
                amount: formatFixed(amount, USDC_DP),
                valid_before_ms: Number(signed.validBeforeS) * 1000,
                nonce: signed.nonce,
                signature: signed.signature,
              }),
            )
        await refreshWrapper(await untilMined(loaded, receipt.tx_hash as `0x${string}`))
        return kind === 'WITHDRAWAL' ? (withdrawalReceiptOf(receipt) ?? true) : true
      } catch (cause: unknown) {
        failFunding(kind === 'DEPOSIT' ? 'ADD' : 'WITHDRAW', messageFor(cause))
        void refreshWrapper()
        return false
      } finally {
        setBusy(false)
      }
    },
    [config, failFunding, guardChain, refreshWrapper, runtime, wrapper.backingAddress],
  )

  /**
   * "Add funds". Gasless when the venue relays; otherwise the direct path, in
   * which the panel shows the "Allow USDC" step first when USDU may not yet
   * take the amount.
   */
  const addFunds = useCallback(
    async (amount: bigint) => {
      if (relayEnabled) return (await sendRelay('DEPOSIT', amount)) !== false
      return deposit(amount)
    },
    [relayEnabled, sendRelay, deposit],
  )

  const withdraw = useCallback(
    async (amount: bigint) => {
      if (relayEnabled) return sendRelay('WITHDRAWAL', amount)
      return requestUnwrap(amount)
    },
    [relayEnabled, sendRelay, requestUnwrap],
  )

  // --- trading setup -------------------------------------------------------

  const [reading, setReading] = useState(false)
  const [setupNeed, setSetupNeed] = useState(0n)
  const [planned, setPlanned] = useState<readonly SetupStep[]>([])

  const refreshChainState = useCallback(
    async (load = false) => {
      if (!config && !load) return
      setReading(true)
      try {
        await refreshWrapper()
      } finally {
        setReading(false)
      }
    },
    [config, refreshWrapper],
  )

  const remaining = useMemo(
    () =>
      missingSetup({
        keyArmed: identity.key !== 'ABSENT',
        usdc: wrapper.backing,
        tradingBalance: wrapper.collateral,
        neededMicroUsdc: setupNeed,
      }),
    [identity.key, wrapper, setupNeed],
  )

  const promptEnableTrading = useCallback(
    (neededMicroUsdc?: unknown) => {
      setSetupNeed(typeof neededMicroUsdc === 'bigint' ? neededMicroUsdc : 0n)
      setPlanned([])
      setError(null)
      setEnabling(true)
      // Set before the read starts, so the close below cannot fire on the
      // facts as they stood before it.
      setReading(true)
      void refreshChainState(true)
    },
    [refreshChainState],
  )

  /**
   * The setup modal's "Switch to … and enable trading", and the ticket's
   * `SWITCH NETWORK`. The session key is signed by the wallet, which refuses the
   * signature on any network but the one in its domain. So switch first, then
   * arm, and stop with the reason if the switch did not land.
   */
  const switchAndArm = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      const loaded = config ?? (await loadWalletConfig())
      setConfig(loaded)
      await ensureVenueChain(loaded)
    } catch (cause: unknown) {
      setError(messageFor(cause))
      setBusy(false)
      return
    }
    await arm()
  }, [config, ensureVenueChain, arm])

  const switchAndEnableTrading = useCallback(
    (neededMicroUsdc?: unknown) => {
      promptEnableTrading(neededMicroUsdc)
      void switchAndArm()
    },
    [promptEnableTrading, switchAndArm],
  )

  // Both derived during render rather than in an effect, the way the ticket
  // resets its price on a side change — each converges in one extra pass.
  //
  // The list the modal shows is every step this run has needed, so a step
  // stays on it, ticked, after it is done.
  if (enabling) {
    const added = remaining.filter((step) => !planned.includes(step))
    if (added.length > 0) setPlanned([...planned, ...added])
  }
  // Complete once every step this run needed is done — and only once the reads
  // have landed and no wallet request is open. The modal then shows its ready
  // tick and closes itself. Never on opening with nothing planned: that is the
  // modal disagreeing with the ticket, and closing it a second after the click
  // read as the button doing nothing.
  const setupComplete = enabling && planned.length > 0 && remaining.length === 0 && !reading && !busy

  const value = useMemo<WalletContextValue>(
    () => ({
      connect,
      signIn: () => setSigningIn(true),
      promptEnableTrading,
      switchAndEnableTrading,
      switchToVenueChain,
      chooseUsername,
      logout,
      revoke,
      busy,
      refreshChainState,
      relayEnabled,
      addFunds,
      withdraw,
      chainMismatch,
      chainTrust,
      trustCurrentAddresses,
      fundingFailure,
      wrapper,
      refreshWrapper,
      approveBacking,
      deposit,
      requestUnwrap,
      finishUnwrap,
      cancelUnwrap,
    }),
    [
      connect,
      promptEnableTrading,
      switchAndEnableTrading,
      switchToVenueChain,
      chooseUsername,
      logout,
      revoke,
      busy,
      refreshChainState,
      relayEnabled,
      addFunds,
      withdraw,
      chainMismatch,
      chainTrust,
      trustCurrentAddresses,
      fundingFailure,
      wrapper,
      refreshWrapper,
      approveBacking,
      deposit,
      requestUnwrap,
      finishUnwrap,
      cancelUnwrap,
    ],
  )

  return (
    <WalletContext.Provider value={value}>
      {children}
      <ConnectModal
        open={picking}
        onClose={() => setPicking(false)}
        connectors={connectors}
        onSelect={(id) => void select(id)}
        busy={busy}
        error={error}
      />
      <SignInModal
        open={signingIn}
        onClose={() => setSigningIn(false)}
        onConfirm={() => void signIn()}
        busy={busy}
        error={error}
      />
      <TradingSetupModal
        open={enabling}
        onClose={() => setEnabling(false)}
        planned={planned}
        remaining={remaining}
        complete={setupComplete}
        identity={identity}
        ttlMs={runtime.sessionKeyTtlMs}
        wrapper={wrapper}
        busy={busy}
        error={error}
        chainMismatch={chainMismatch}
        chainTrust={chainTrust}
        onTrustAddresses={trustCurrentAddresses}
        relayEnabled={relayEnabled}
        onArm={() => void switchAndArm()}
        onSwitchChain={() => void switchToVenueChain()}
        onAllowFunds={(amount) => void approveBacking(amount)}
        onAddFunds={(amount) => void addFunds(amount)}
      />
      {/* Rendered only while open, so the chunk is fetched on the click and
          never before; no fallback, because a modal that appears a frame
          later is the same as one that appears now. */}
      {choosingName ? (
        <Suspense fallback={null}>
          <ChooseUsernameModal open onClose={() => setChoosingName(false)} />
        </Suspense>
      ) : null}
    </WalletContext.Provider>
  )
}

/**
 * Wallet errors are the one class of failure a user reads in full, so the common
 * ones get plain sentences rather than the SDK's.
 */
function messageFor(cause: unknown): string {
  // Already a sentence written for this screen, and one that would otherwise
  // match the wrong-network pattern below.
  if (cause instanceof ChainSwitchError) return cause.message
  const raw = cause instanceof Error ? cause.message : String(cause)
  if (isRejection(cause)) {
    return 'You declined the request in your wallet.'
  }
  if (/already pending|request of type/i.test(raw)) {
    return 'Your wallet already has a request open. Finish it there first.'
  }
  // A wallet that refused a signature because it is on another network, for
  // example MetaMask's "must match the active chainId". No chain is named here,
  // because the venue's chain is in the payload: the switch button names it.
  if (/chain|network/i.test(raw) && /mismatch|unsupported|switch|must match/i.test(raw)) {
    return 'Your wallet is on the wrong network. Switch networks and try again.'
  }
  // The venue's refusals of a funding request, by code (`POST /chain/*`).
  if (cause instanceof HttpError) {
    switch (cause.code) {
      case 'SIGNATURE_INVALID':
        return 'That signature is not from the account you signed in with. Check the account selected in your wallet.'
      case 'NONCE_REUSED':
        return 'That request was already sent. Your balance updates when it lands.'
      case 'RATE_LIMITED':
        return 'You have used this hour’s free transfers. Try again later, or send it from your own wallet.'
      case 'SERVICE_UNAVAILABLE':
        return 'We could not send that right now, and nothing moved. Try again, or send it from your own wallet.'
    }
  }
  // §F126's refusals, which the client checks for first and the chain enforces
  // anyway — a clock a second ahead of the chain's, a settlement read a moment
  // too early, or a second tab.
  if (/AwaitingSettlement/.test(raw)) {
    return 'Not yet — the markets this withdrawal waits for have not settled. It is released as soon as they have.'
  }
  if (/UnwrapNotReady/.test(raw)) {
    return 'Not yet — the markets this withdrawal waits for are still trading. It is released once they have settled.'
  }
  if (/NothingPending/.test(raw)) {
    return 'There is no withdrawal waiting to finish. It may already have been paid out or cancelled.'
  }
  return raw
}

function mismatchOf(
  targets: ChainTargets | null,
  walletChainId: number | null,
): { wallet: number; venue: number } | null {
  return targets && walletChainId && walletChainId !== targets.chainId ?
      { wallet: walletChainId, venue: targets.chainId }
    : null
}

/** `ensureVenueChain`'s refusals, which name the venue's network. */
class ChainSwitchError extends Error {}

/** EIP-1193's 4001, by code or by the message wallets put on it. */
function isRejection(cause: unknown): boolean {
  for (let c: unknown = cause; c; c = (c as { cause?: unknown }).cause) {
    if ((c as { code?: unknown }).code === 4001) return true
    if ((c as { name?: unknown }).name === 'UserRejectedRequestError') return true
  }
  const raw = cause instanceof Error ? cause.message : String(cause)
  return /user rejected|user denied|rejected the request/i.test(raw)
}

/**
 * A fresh read, with every field it could not answer taken from the last one.
 *
 * Each chain read fails independently into `null` ("unread"), and a read
 * through the wallet can time out under load. Letting that overwrite a value an
 * earlier read established made the setup modal decide, a second after opening,
 * that there was nothing left to do — and close. A failed read has not told us
 * anything changed.
 */
function keepKnown<T extends object>(prev: T, next: T): T {
  const merged = { ...next }
  for (const key of Object.keys(prev) as (keyof T)[]) {
    const value = next[key]
    if (value === null || value === 'UNKNOWN') merged[key] = prev[key]
  }
  return merged
}
